import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICtaTemplateApiDeliverables } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { ComponentStore } from '@ngrx/component-store';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { HttpUtilitiesService, IGenericCrudRequestConstructionParameters } from '@utilities/http-utilities.service';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { TCampaignApi } from './campaigns.interface';
import { CampaignsService } from './campaigns.service';

/**
 * Maps every API deliverable field to its backend join relation name.
 * `Record<keyof ICtaTemplateApiDeliverables, string>` requires all keys —
 * adding a field to `ICtaTemplateApiDeliverables` without an entry here is a compile error.
 */
const CTA_TEMPLATE_DELIVERABLE_JOINS: Record<keyof ICtaTemplateApiDeliverables, string> = {
  audioTrackings: 'cta_templates.audio_trackings',
  captions: 'cta_templates.captions',
  chatbotMultis: 'cta_templates.chatbot_multis',
  chatbots: 'cta_templates.chatbots',
  contents: 'cta_templates.contents',
  customs: 'cta_templates.customs',
  descriptions: 'cta_templates.descriptions',
  gamePlays: 'cta_templates.game_plays',
  hashTags: 'cta_templates.hash_tags',
  mentions: 'cta_templates.mentions',
  messages: 'cta_templates.messages',
  productPlacements: 'cta_templates.product_placements',
  showcases: 'cta_templates.showcases',
  talkingPointAis: 'cta_templates.talking_point_ais',
  talkingPoints: 'cta_templates.talking_points',
  textTrackings: 'cta_templates.text_trackings',
  titles: 'cta_templates.titles',
  visuals: 'cta_templates.visuals',
};

interface ICampaignsState {
  campaigns: TCampaignApi[];
  campaignsLoading: boolean;
  error: string | null;
  fetchingCampaignsForAnalytics: boolean;
  loading: boolean;
  nonLiveCampaignsMap: Map<string, TCampaignApi>;
  /**
   * Full (limit-1000, ctaTemplates-joined) campaign list for creator/customer "profile" pages'
   * own campaigns()/hasUnfilteredCampaigns() signals and the CTA Analytics dialog - see
   * fetchProfileCampaigns for why this is kept separate from `campaigns`/`total`.
   */
  profileCampaigns: TCampaignApi[];
  /**
   * Own loading flag for fetchProfileCampaigns, kept separate from campaignsLoading/loading/
   * fetchingCampaignsForAnalytics: those are shared by fetchCampaigns' single switchMap, where
   * only one call is ever in flight at a time, so resetting them to false on completion is safe.
   * fetchProfileCampaigns runs on its own switchMap and can now be in flight at the same time as
   * fetchCampaigns - if it reset those shared flags too, whichever fetch finished first would
   * incorrectly mark the other as no-longer-loading while its response was still pending.
   */
  profileCampaignsLoading: boolean;
  total: number;
}

export interface IFetchCampaignsParams {
  creatorId?: string;
  customerId?: string;
  ctaTemplates?: boolean;
  campaignId?: string;
  campaignIds?: string[];
  campaignName?: string;
  adminMode?: boolean;
  creatorMode?: boolean;
  params?: IGenericCrudRequestConstructionParameters;
  isForAnalytics?: boolean;
  joins?: string[];
}

const initialState: ICampaignsState = {
  campaigns: [],
  campaignsLoading: false,
  error: null,
  fetchingCampaignsForAnalytics: true,
  loading: false,
  nonLiveCampaignsMap: new Map<string, TCampaignApi>(),
  profileCampaigns: [],
  profileCampaignsLoading: false,
  total: 0,
};

@Injectable()
export class CampaignsStore extends ComponentStore<ICampaignsState> {
  public readonly campaigns$ = this.selectSignal((state: ICampaignsState) => state.campaigns);
  public readonly campaignsLoading$ = this.selectSignal((state: ICampaignsState) => state.campaignsLoading);
  public readonly nonLiveCampaignsMap$ = this.selectSignal((state: ICampaignsState) => state.nonLiveCampaignsMap);
  public readonly profileCampaigns$ = this.selectSignal((state: ICampaignsState) => state.profileCampaigns);
  public readonly profileCampaignsLoading$ = this.selectSignal(
    (state: ICampaignsState) => state.profileCampaignsLoading,
  );
  public readonly loading$ = this.selectSignal((state: ICampaignsState) => state.loading);
  public readonly fetchingCampaignsForAnalytics$ = this.selectSignal(
    (state: ICampaignsState) => state.fetchingCampaignsForAnalytics,
  );
  public readonly error$ = this.selectSignal((state: ICampaignsState) => state.error);
  public readonly total$ = this.selectSignal((state: ICampaignsState) => state.total);

  public readonly fetchCampaigns = this.effect((params$: Observable<IFetchCampaignsParams>) => {
    return params$.pipe(
      tap((params) =>
        this.patchState({
          campaignsLoading: !params.isForAnalytics,
          error: null,
          fetchingCampaignsForAnalytics: params.isForAnalytics,
          loading: true,
        }),
      ),
      switchMap((params) => {
        const httpParams = this.buildParams({ ...params, joins: ['customer'] });
        const fetch$ = params.adminMode
          ? this.campaignsService.getAdminCampaigns(httpParams)
          : params.creatorMode
            ? this.campaignsService.getCreatorBriefingCampaigns(httpParams)
            : this.campaignsService.getCampaigns(httpParams);

        return fetch$.pipe(
          tap((campaigns: IApiResponse<TCampaignApi[]> | null) => {
            this.handleCampaignsResponse(campaigns, params);
          }),
          catchError(() => this.handleError()),
        );
      }),
    );
  });

  /**
   * Separate fetch pipeline for the creator/customer "profile" pages' own full campaign list
   * (limit-1000, ctaTemplates-joined - used for their own campaigns()/hasUnfilteredCampaigns()
   * signals and the CTA Analytics dialog, not for the Campaigns tab's paginated table). Kept on
   * its own switchMap - rather than reusing `fetchCampaigns` - because that effect's datatable
   * callers (CampaignsDatatableComponent's own page/sort/filter-driven fetches) share the same
   * CampaignsStore instance at CreatorDetailsComponent/CustomerDetailsComponent level: a single
   * shared switchMap meant whichever of the two calls landed second always cancelled the other's
   * in-flight request (visible in devtools as one campaigns request being aborted right after
   * page load) and, worse, whichever response arrived last silently overwrote `campaigns`/`total`
   * with its own (differently-scoped) data regardless of which fetch it came from. Writes to its
   * own `profileCampaigns`/`profileCampaignsLoading` fields instead of the shared
   * campaignsLoading/loading/fetchingCampaignsForAnalytics ones - see profileCampaignsLoading's
   * own doc comment for why those can't be shared once this runs on an independent switchMap.
   */
  public readonly fetchProfileCampaigns = this.effect((params$: Observable<IFetchCampaignsParams>) => {
    return params$.pipe(
      tap(() => this.patchState({ error: null, profileCampaignsLoading: true })),
      switchMap((params) => {
        const httpParams = this.buildParams({ ...params, joins: ['customer'] });
        const fetch$ = params.adminMode
          ? this.campaignsService.getAdminCampaigns(httpParams)
          : params.creatorMode
            ? this.campaignsService.getCreatorBriefingCampaigns(httpParams)
            : this.campaignsService.getCampaigns(httpParams);

        return fetch$.pipe(
          tap((campaigns: IApiResponse<TCampaignApi[]> | null) => {
            this.handleProfileCampaignsResponse(campaigns);
          }),
          catchError(() => this.handleProfileCampaignsError()),
        );
      }),
    );
  });

  public readonly deleteCampaign = this.effect(
    (campaignId$: Observable<{ campaignId: string; params: IFetchCampaignsParams }>) => {
      return campaignId$.pipe(
        tap(() => this.patchState({ error: null, loading: true })),
        switchMap(({ campaignId, params }) =>
          this.campaignsService.deleteCampaign(campaignId).pipe(
            tap(() => {
              this.patchState({ loading: false });
              this.fetchCampaigns(params);
              this.eventTrackingService.track(EEventType.campaignDeleted, {
                campaignId,
                source: 'campaign_list_row',
              });
            }),
            catchError(() => {
              this.patchState({
                error: 'Failed to delete campaign',
                loading: false,
              });

              return EMPTY;
            }),
          ),
        ),
      );
    },
  );

  constructor(
    private readonly campaignsService: CampaignsService,
    private readonly dateUtils: DateUtilitiesService,
    private readonly httpUtilitiesService: HttpUtilitiesService,
    private readonly eventTrackingService: EventTrackingService,
  ) {
    super(initialState);
  }

  private handleError(): Observable<never> {
    this.patchState({
      campaignsLoading: false,
      error: 'Failed to load campaigns',
      loading: false,
    });

    return EMPTY;
  }

  private handleCampaignsResponse(
    campaigns: IApiResponse<TCampaignApi[]> | null,
    params?: IFetchCampaignsParams,
  ): void {
    const mappedCampaigns: TCampaignApi[] = this.mapCampaignsResponse(campaigns);

    this.patchState({
      ...(params?.isForAnalytics
        ? { nonLiveCampaignsMap: new Map(mappedCampaigns.map((campaign) => [campaign.id, campaign])) }
        : { campaigns: mappedCampaigns, total: campaigns?.meta?.total ?? 0 }),
      campaignsLoading: false,
      fetchingCampaignsForAnalytics: false,
      loading: false,
    });
  }

  private handleProfileCampaignsResponse(campaigns: IApiResponse<TCampaignApi[]> | null): void {
    this.patchState({
      profileCampaigns: this.mapCampaignsResponse(campaigns),
      profileCampaignsLoading: false,
    });
  }

  private handleProfileCampaignsError(): Observable<never> {
    this.patchState({
      error: 'Failed to load campaigns',
      profileCampaignsLoading: false,
    });

    return EMPTY;
  }

  private mapCampaignsResponse(campaigns: IApiResponse<TCampaignApi[]> | null): TCampaignApi[] {
    return (
      campaigns?.data?.map((campaign) => ({
        ...campaign,
        end: this.dateUtils.parseDateTimeFromApi(campaign.end as string),
        start: this.dateUtils.parseDateTimeFromApi(campaign.start as string),
      })) ?? []
    );
  }

  private buildParams(params: IFetchCampaignsParams): HttpParams {
    const {
      creatorId,
      customerId,
      campaignId,
      campaignIds,
      campaignName,
      ctaTemplates,
      params: genericParams,
    } = params;
    let httpParams = new HttpParams().set('limit', '1000').set('order_by', '-created_at').set('joins', 'customer');

    if (genericParams) {
      httpParams = this.httpUtilitiesService.insertGenericCrudRequestParameters(genericParams);
    }

    if (creatorId) {
      httpParams = httpParams.set('campaign_creators__creator_id', creatorId);
    }

    if (customerId) {
      httpParams = httpParams.set('customer__id', customerId);
    }

    if (campaignId) {
      httpParams = httpParams.set('id', campaignId);
    }

    if (campaignIds) {
      httpParams = httpParams.set('id__in', campaignIds.join(','));
    }

    if (campaignName || genericParams?.search?.searchText) {
      httpParams = httpParams.set('name__ilike', campaignName || genericParams?.search?.searchText || '');
    }

    if (params.joins?.length) {
      httpParams = httpParams.has('joins')
        ? httpParams.set('joins', `${httpParams.get('joins')},${params.joins.join(',')}`)
        : httpParams.set('joins', params.joins.join(','));
    }

    if (ctaTemplates) {
      const joins = [
        'customer',
        'campaign_creators',
        'campaign_creators.creator',
        'campaign_creators.creator.channels',
        'cta_templates',
        'cta_templates.cta_creators',
        'cta_templates.executions',
        'cta_templates.executions.expected_kpis',
        ...Object.values(CTA_TEMPLATE_DELIVERABLE_JOINS),
        ...(params.joins ?? []),
      ];

      httpParams = httpParams.set('joins', joins.join(','));
    }

    return httpParams;
  }
}
